import { useState, useEffect, act } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StepProps } from "@/lib/builder/models/builder.model";
import { DayActivity } from "@/lib/builder/models/activity.model";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { ItineraryTotals } from "./models/itinerary.model";

const CheckoutStep = ({
    data,
    onDataChange
}: StepProps) => {
    const [activitiesWithPrice, setActivitiesWithPrice] = useState<DayActivity[]>([]);
    const [updatedTotals, setUpdatedTotals] = useState<ItineraryTotals>({
        commisionable_total: 0,
        not_commisionable_total: 0,
        p2b_commission_percentage: 22,
        p2b_commission_amount: 0,
        td_fee_percentage: 70,
        total: 0,
    });

    useEffect(() => {
        const activitiesWithPriceEnabled = data.extractAllActivities().filter(activity => activity.include_price && activity.cost > 0);
        setActivitiesWithPrice(activitiesWithPriceEnabled);
        setUpdatedTotals(data.extractTotals());
    }, [data]);

    // Helper function to safely access cost property
    const getActivityCost = (activity: DayActivity): number => {
        return (activity as { cost?: number }).cost || 0;
    };

    return (
        <div className="w-full max-w-xl mx-auto p-5">
            {/* Checkout Card */}
            <Card className="w-full">
                <CardHeader>
                    <CardTitle className="text-sm font-medium text-black">Total</CardTitle>
                </CardHeader>

                <CardContent className="space-y-6">
                    <div className="max-h-64 overflow-y-auto space-y-4 pr-2">
                        {activitiesWithPrice?.map((activity) => (
                            <div key={activity.id} className="flex items-center justify-between">
                                <span className="text-sm text-gray-900 font-medium">
                                    {activity.title}
                                </span>
                                <span className="text-sm text-gray-900 font-medium">
                                    €{getActivityCost(activity).toLocaleString()}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Separator */}
                    <div className="border-t border-gray-300"></div>

                    <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-black">Commissionable total</span>
                            <span className="text-sm font-medium text-gray-900">
                                €{updatedTotals?.commisionable_total?.toLocaleString() ?? '-'}
                            </span>
                        </div>
                    </div>

                    {/* Your Earnings */}
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-black">Total commission</span>
                            <span className="text-sm font-medium text-gray-900">
                                €{updatedTotals?.p2b_commission_amount?.toLocaleString() ?? '-'}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500">
                            ({updatedTotals?.p2b_commission_percentage ?? '20'}% of Commissionable total)
                        </div>
                    </div>

                    {/* Your Earnings */}
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-black">Your Earnings</span>
                            <span className="text-sm font-medium text-gray-900">
                                €{((updatedTotals.td_fee_percentage / 100) * updatedTotals.p2b_commission_amount)?.toLocaleString() ?? '-'}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500">
                            ({updatedTotals?.td_fee_percentage ?? '70'}% of Total commission)
                        </div>
                    </div>

                    {/* Separator */}
                    <div className="border-t border-gray-300"></div>

                    {/* Total (with fee) */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-black">Total</span>
                        <span className="text-sm font-medium text-gray-900">
                            €{(updatedTotals?.total)?.toLocaleString() ?? '-'}
                        </span>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default CheckoutStep;
